import {
  Box,
  Button,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { foyerApi, type DeviceLogLine } from "../http/foyer";
import { useFormat } from "../i18n/format";
import { errorText } from "../utils/apiError";

interface Props {
  premiseId: string;
  screenId: string;
}

type Level = "problems" | "all";

const PAGE = 200;

/**
 * The TV's continuously shipped log (warn/error plus player and signage
 * lines, kept 14 days), read newest first.
 *
 * "Capture logs" in the Actions list is a snapshot taken now. This panel
 * shows what the box already sent over the past two weeks, so it covers the
 * case where nobody captured anything at the moment a problem happened.
 */
export default function DeviceLogPanel({ premiseId, screenId }: Props) {
  const { t } = useTranslation(["premises", "common"]);
  const fmt = useFormat();
  const [level, setLevel] = useState<Level>("problems");
  const [lines, setLines] = useState<DeviceLogLine[]>([]);
  const [nextBefore, setNextBefore] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(
    async (before?: string) => {
      setLoading(true);
      setError(null);
      try {
        const page = await foyerApi.screenLogEntries(premiseId, screenId, {
          level,
          before,
          limit: PAGE,
        });
        setLines((prev) => (before ? [...prev, ...page.lines] : page.lines));
        setNextBefore(page.nextBefore);
      } catch (err) {
        setError(errorText(err, t));
      } finally {
        setLoading(false);
      }
    },
    [premiseId, screenId, level, t]
  );

  useEffect(() => {
    setLines([]);
    setNextBefore(undefined);
    void load();
  }, [load]);

  // Text filter over what is already loaded. Server-side text search would
  // scan an unindexed field across up to hundreds of thousands of rows.
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? lines.filter((l) => l.message.toLowerCase().includes(q)) : lines;
  }, [lines, filter]);

  return (
    <Box data-testid="device-log-panel">
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 1, flexWrap: "wrap", rowGap: 1 }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={level}
          onChange={(_, v: Level | null) => v && setLevel(v)}
        >
          <ToggleButton value="problems">
            {t("deviceLogPanel.level.problems")}
          </ToggleButton>
          <ToggleButton value="all">
            {t("deviceLogPanel.level.all")}
          </ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small"
          placeholder={t("deviceLogPanel.filterPlaceholder")}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ minWidth: 180, flex: 1 }}
        />
        <Button size="small" onClick={() => void load()} disabled={loading}>
          {t("common:buttons.refresh")}
        </Button>
      </Stack>

      {error && (
        <Typography
          variant="caption"
          color="error.main"
          sx={{ display: "block" }}
        >
          {t("deviceLogPanel.loadFailed", { reason: error })}
        </Typography>
      )}

      {!error && !loading && lines.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {level === "problems"
            ? t("deviceLogPanel.emptyProblems")
            : t("deviceLogPanel.emptyAll")}
        </Typography>
      )}

      {shown.length > 0 && (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1,
            maxHeight: 320,
            overflow: "auto",
            typography: "micro",
            lineHeight: 1.45,
            fontFamily: "fontFamilyMono",
            bgcolor: "action.hover",
            borderRadius: 1,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {shown.map((l, i) => (
            <Box
              component="span"
              key={`${l.ts}-${i}`}
              sx={{
                display: "block",
                color:
                  l.level === "error"
                    ? "error.main"
                    : l.level === "warn"
                      ? "warning.main"
                      : "text.primary",
              }}
            >
              {`${fmt.dateTime(l.ts, { dateStyle: "short", timeStyle: "medium" })} [${l.level}] ${l.message}`}
            </Box>
          ))}
        </Box>
      )}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
        {nextBefore && (
          <Button
            size="small"
            onClick={() => void load(nextBefore)}
            disabled={loading}
          >
            {t("deviceLogPanel.loadOlder")}
          </Button>
        )}
        {lines.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {filter.trim()
              ? t("deviceLogPanel.shownOfLoaded", {
                  shown: shown.length,
                  total: lines.length,
                })
              : t("deviceLogPanel.loaded", { count: lines.length })}
            {t("deviceLogPanel.deviceClock")}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
